import _ from 'lodash';
import { useMutation, useQuery } from '@apollo/client';

import { rejectGraphQLErrors } from '../helpers/graphql-error.helpers';
import { COUNT_HAS_UNSENT_TYPE, REQUEST_EMAILS, SEND_COMMUNICATION } from '../queries/email-log.queries';
import { getAuthUser } from './auth.service';
import { useActiveAgeGroupIdsInAssessment } from './age-group.service';

// The player page's Emails & Text Messages list. Any write that adds or changes a player's messages refetches it.
export const COMMUNICATION_LIST_QUERIES = ['GetCommunicationList'];

export const buildRequestEmails = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mutation, rest] = useMutation(REQUEST_EMAILS);

  return {
    ...rest,
    requestEmails: (email: string, types: string[], operationId: string) => mutation({
      variables: { email, types, operationId }
    })
  };
};

// Without a single age group, the count and the send both target the active selection narrowed to
// the active assessment — the same list, so the count shown is what a send would reach.
const useTargetAgeGroupIds = (ageGroupId?: string) => {
  const selection = useActiveAgeGroupIdsInAssessment({ skip: Boolean(ageGroupId) });

  return ageGroupId ? { ageGroupIds: [ageGroupId], loading: false, error: undefined } : selection;
};

export const getCountHasUnsentType = (type: string, ageGroupId?: string, playerAssessmentIds?: string[]) => {
  const user = getAuthUser();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const target = useTargetAgeGroupIds(ageGroupId);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { data, error: countError, ...rest } = useQuery(COUNT_HAS_UNSENT_TYPE, {
    // An empty target has nothing to count, and the server would answer for no age group.
    skip: !user || !target.ageGroupIds?.length,
    variables: {
      assessmentId: user?.settings.activeAssessmentId,
      ageGroupIds: target.ageGroupIds,
      playerAssessmentIds,
      type
    },
    fetchPolicy: 'cache-and-network'
  });
  const error = countError || target.error;

  // On error `data` may still hold a stale count, and a stale 0 reads as "all sent", so `count` is null.
  // Callers gate on `error` and show an explicit failure message instead.
  return {
    ...rest,
    loading: rest.loading || target.loading,
    error,
    ageGroupIds: error ? undefined : target.ageGroupIds,
    count: error ? null : _.get(data, 'countHasUnsentType', 0)
  };
};

// Welcome, level and team sends. Report cards go through `buildSendReportCards`, which reports who was told.
export const buildSendEmails = () => {
  const user = getAuthUser();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const selection = useActiveAgeGroupIdsInAssessment();
  // A send can save some writes and still report errors: errorPolicy 'all' keeps the result, and sendEmails rejects with the errors.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mutation, rest] = useMutation(SEND_COMMUNICATION, { errorPolicy: 'all' });

  return {
    ...rest,
    sendEmails: (type: string, ageGroupIds?: string | string[]) => {
      const targetAgeGroupIds = _.isNil(ageGroupIds) ? selection.ageGroupIds : _.castArray(ageGroupIds);

      // Never the raw selection: its other assessments' picks make the server refuse the whole send.
      if (!targetAgeGroupIds?.length) {
        return Promise.reject(new Error(targetAgeGroupIds ? 'No age groups in this assessment are selected.' : 'Age groups are still loading.'));
      }

      return mutation({
        variables: { ageGroupIds: targetAgeGroupIds, assessmentId: user?.settings.activeAssessmentId, type },
      }).then(rejectGraphQLErrors);
    }
  }
};
